import { createRouter, createWebHistory } from 'vue-router';
import type { RouteRecordRaw } from 'vue-router';
import { PermissionService } from '../modules/auth/services/permission.service';
import { appendAuthInterceptor } from './interceptors';

const permissionServices = new PermissionService();

const routes: Array<RouteRecordRaw> = [
    {
        path: '/',
        name: 'Home',
        meta: {
            requiresAuth: true,
            roles: [...permissionServices.getPermissionRoles('dashboard.view')],
        },
        component: () => import('../modules/layout/views/MainComponent.vue'),
    },
    {
        path: '/auth',
        name: 'Auth',
        redirect: '/',
        children: [
            {
                path: 'msal-logout',
                name: 'MSALLogout',
                meta: {
                    requiresAuth: true,
                    roles: [import.meta.env.VITE_MSAL_ADMIN_ROLE_NAME, import.meta.env.VITE_MSAL_READER_ROLE_NAME],
                },
                component: () => import('../modules/auth/views/MSALLogout.vue'),
            },
        ],
    },
    {
        path: '/machines',
        name: 'Machines',
        meta: {
            requiresAuth: true,
            roles: [...permissionServices.getPermissionRoles('machines.read')],
        },
        children: [
            {
                path: '',
                name: 'MachinesHome',
                meta: {
                    requiresAuth: true,
                    roles: [...permissionServices.getPermissionRoles('machines.read')],
                },
                component: () => import('../modules/machines/views/MachinesHome.vue'),
            },
            {
                path: 'create',
                name: 'CreateMachine',
                meta: {
                    requiresAuth: true,
                    roles: [...permissionServices.getPermissionRoles('machines.create')],
                },
                component: () => import('../modules/machines/views/CreateMachine.vue'),
            },
            {
                path: 'update/:id',
                name: 'UpdateMachine',
                meta: {
                    requiresAuth: true,
                    roles: [...permissionServices.getPermissionRoles('machines.update')],
                },
                component: () => import('../modules/machines/views/UpdateMachine.vue'),
            },
            {
                path: 'view/:id',
                name: 'ViewMachine',
                meta: {
                    requiresAuth: true,
                    roles: [...permissionServices.getPermissionRoles('machines.view')],
                },
                component: () => import('../modules/machines/views/ViewMachine.vue'),
            },
        ],
    },
    {
        path: '/implements',
        name: 'Implements',
        meta: {
            requiresAuth: true,
            roles: [...permissionServices.getPermissionRoles('implements.read')],
        },
        children: [
            {
                path: '',
                name: 'ImplementsHome',
                meta: {
                    requiresAuth: true,
                    roles: [...permissionServices.getPermissionRoles('implements.read')],
                },
                component: () => import('../modules/implements/views/ImplementsHome.vue'),
            },
            {
                path: 'create',
                name: 'CreateImplement',
                meta: {
                    requiresAuth: true,
                    roles: [...permissionServices.getPermissionRoles('implements.create')],
                },
                component: () => import('../modules/implements/views/CreateImplement.vue'),
            },
            {
                path: 'view/:id',
                name: 'ViewImplement',
                meta: {
                    requiresAuth: true,
                    roles: [...permissionServices.getPermissionRoles('implements.view')],
                },
                component: () => import('../modules/implements/views/ViewImplement.vue'),
            },
            {
                path: 'update/:id',
                name: 'UpdateImplement',
                meta: {
                    requiresAuth: true,
                    roles: [...permissionServices.getPermissionRoles('implements.update')],
                },
                component: () => import('../modules/implements/views/UpdateImplement.vue'),
            },
        ],
    },
    {
        path: '/compatibility',
        name: 'Compatibility',
        meta: {
            requiresAuth: true,
            roles: [...permissionServices.getPermissionRoles('compatibility.read')],
        },
        component: () => import('../modules/compatibility/views/CompatibilityHome.vue'),
    },
    {
        path: '/bulk-update',
        name: 'BulkUpdate',
        meta: {
            requiresAuth: true,
            roles: [...permissionServices.getPermissionRoles('bulk_update.read')],
        },
        component: () => import('../modules/bulk-update/views/BulkUpdate.vue'),
    },
    { path: '/:pathMatch(.*)*', name: 'NotFound', component: () => import('../views/PageNotFound.vue') },
];

// Create router instance
const router = createRouter({
    history: createWebHistory(import.meta.env.BASE_URL),
    routes,
});

appendAuthInterceptor(router);

export default router;
